import { useMemo } from 'react';
import { Goal, GoalEventType } from 'modules/goals/types';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { useQueryGoalMetrics } from 'modules/missionControl/hooks/useQueryGoalMetrics';
import {
  useQueryGoalTopCampaign,
  TopCampaign as TopCampaignType,
} from 'modules/missionControl/hooks/useQueryGoalTopCampaign';
import {
  MetricsDetails,
  getTimePeriodText,
} from 'modules/missionControl/lib/components/MetricsDetails';
import { calculateDelta } from 'modules/missionControl/utils/calculations';
import { LineChart } from 'modules/missionControl/lib/components/charts/LineChart';
import { Overview } from 'ui/illustrations/Overview';
import { GOAL_CARD_TEST_IDS } from './GoalCard.constants';
import {
  DetailsWrapper,
  StyledDashboardCard,
  StyledPlaceholderState,
  TotalText,
} from './GoalCard.styled';
import { getGoalChartOptions, parseMetricsForGraph } from './GoalCard.utils';
import { Header } from './Header';
import { TopCampaign } from './TopCampaign';
import { GoalCardLoading } from './GoalCardLoading';

type GoalCardProps = {
  goal: Goal;
  isLastCard?: boolean;
};

export const GoalCard = ({ goal, isLastCard, ...props }: GoalCardProps) => {
  const {
    startDate,
    endDate,
    labelIds,
    messageTypeIds,
    dateRangePreset,
    topCampaignsBySystemEventGoal,
  } = useMissionControlContext();

  const queryGoalMetricsParams = useMemo(
    () => ({
      goalId: goal.id,
      startTime: startDate?.utc().format() as string,
      endTime: endDate?.utc().format() as string,
      messageTypeIds,
      campaignLabelIds: labelIds,
      includePrevious: true,
      eventType: goal.eventType,
      conversionEvent: goal.eventName,
    }),
    [startDate, endDate, messageTypeIds, labelIds, goal?.id, goal?.eventType, goal?.eventName],
  );

  const queryGoalTopCampaignParams = useMemo(
    () => ({
      conversionEvent: goal.eventName,
      eventType: goal.eventType,
      startTime: startDate?.utc().format() as string,
      endTime: endDate?.utc().format() as string,
      messageTypeIds,
      campaignLabelIds: labelIds,
    }),
    [startDate, endDate, goal?.eventName, goal?.eventType, messageTypeIds, labelIds],
  );

  const { goalMetrics, isLoading } = useQueryGoalMetrics(queryGoalMetricsParams, {
    enabled: !!goal?.id,
  });

  const { topCampaign } = useQueryGoalTopCampaign(queryGoalTopCampaignParams, {
    enabled: goal?.eventType !== GoalEventType.System,
  });

  const topCampaignForGoal = useMemo<TopCampaignType | undefined>(() => {
    if (goal.eventType === GoalEventType.System) {
      return topCampaignsBySystemEventGoal[goal.id];
    }
    return topCampaign;
  }, [topCampaignsBySystemEventGoal, topCampaign, goal.id, goal.eventType]);

  const metric = goal?.metricDefinition?.metric;
  const associatedMetric = goal?.metricDefinition?.associatedMetric ?? '';

  const currentMetric = goalMetrics?.currentMetrics?.[metric];
  const previousMetric = goalMetrics?.previousMetrics?.[metric];
  const associatedMetrics = goalMetrics?.currentMetrics?.[associatedMetric]; // associatedMetrics are the same as currentMetrics

  const associatedMetricLabel = goal?.metricDefinition?.associatedMetricLabel ?? '';
  const associatedMetricValue = associatedMetrics?.value ?? 0;

  const hasEvents = (goalMetrics?.currentMetrics?.[metric]?.value ?? 0) > 0;

  const delta = calculateDelta(currentMetric?.value ?? 0, previousMetric?.value);

  const seriesOptions = useMemo(
    () => parseMetricsForGraph(metric, currentMetric),
    [metric, currentMetric],
  );

  if (isLoading) {
    return <GoalCardLoading />;
  }

  return (
    <StyledDashboardCard data-test={GOAL_CARD_TEST_IDS.CARD} {...props}>
      <Header goal={goal} />
      {hasEvents && (
        <DetailsWrapper>
          <TotalText>{metric}</TotalText>
          <MetricsDetails
            delta={delta}
            metricName={goal.eventName}
            metricValue={currentMetric?.value}
            timePeriod={getTimePeriodText(dateRangePreset)}
          />
        </DetailsWrapper>
      )}

      {hasEvents ? (
        <>
          <LineChart
            chartOptions={getGoalChartOptions()}
            data={[seriesOptions]}
            // The line chart size does not always update when cards are added/removed, so we need to reflow it manually
            shouldReflow={isLastCard}
          />

          {!!topCampaignForGoal && (
            <TopCampaign
              eventType={goal.eventType}
              topCampaign={topCampaignForGoal}
              associatedMetricValue={associatedMetricValue}
              associatedMetricLabel={associatedMetricLabel}
            />
          )}
        </>
      ) : (
        <StyledPlaceholderState
          data-test="no-events-placeholder"
          Illustration={Overview}
          title={
            goal.eventType === GoalEventType.System ? 'No engagement, yet' : 'No conversions, yet'
          }
        >
          This may be because the goal or its associated campaigns are new, or filters are hiding
          results.
        </StyledPlaceholderState>
      )}
    </StyledDashboardCard>
  );
};
